/**
 * guestPortalV2Demo — el armazón del portal del huésped (PRODUCT_V2_SPEC §7.2).
 *
 * Aquí NO hay datos de huésped, ni de estancia, ni de llegada. Todo eso vive en
 * `guestsV2Demo` / `pmsDemoData` / `settingsV2Demo` y el portal lo deriva. Este
 * módulo solo declara lo que es propio del portal: qué áreas existen, cuáles
 * están construidas, y el vocabulario con el que se le habla al huésped.
 *
 * Por qué el portal no es una pantalla de admin con otro CSS: el huésped no
 * tiene contexto operativo. No sabe qué es `pre_arrival`, ni le importa. Ve su
 * estancia y una sola cosa que hacer a continuación.
 */

// ---------------------------------------------------------------------------
// Áreas del portal
// ---------------------------------------------------------------------------

/**
 * Las áreas aprobadas del portal (§7.2), con la fase de la hoja de ruta (§17)
 * en la que se construyen.
 *
 * SOLO se navega a las que están `built`. El resto NO se pinta: ni en gris, ni
 * como "próximamente", ni deshabilitadas. Un portal de huésped con seis
 * entradas muertas no es un portal, es una maqueta — y el huésped que toca
 * "Restaurante" y no pasa nada no entiende que sea "una fase posterior",
 * entiende que la aplicación está rota.
 *
 * El registro existe para que la siguiente fase solo tenga que poner
 * `built: true` y añadir la ruta. Ese es todo el trabajo del armazón.
 *
 * Nota sobre restaurante / F&B: NO está en esta lista a propósito. §18 lo
 * declara un no-objetivo ("A full restaurant / F&B POS"); lo que el portal
 * venderá son servicios del catálogo (§9), y el desayuno es uno de ellos.
 */
export const PORTAL_AREAS = [
  {
    key: "home",
    label: "Tu estancia",
    path: "",
    built: true,
    phase: 5,
  },
  {
    key: "checkin",
    label: "Pre-check-in",
    path: "checkin",
    built: true,
    phase: 5,
  },
  // --- Aprobadas en §7.2, todavía sin construir -----------------------------
  // Se dejan declaradas para que se vea qué viene y en qué orden, no para
  // pintarlas. `built: false` significa que no existe la pantalla.
  { key: "services", label: "Servicios", path: "services", built: false, phase: 6 },
  { key: "requests", label: "Peticiones", path: "requests", built: false, phase: 6 },
  { key: "info", label: "Información", path: "info", built: false, phase: 6 },
  { key: "checkout", label: "Salida", path: "checkout", built: false, phase: 6 },
];

/** Las áreas que de verdad existen hoy. Es lo único que se navega. */
export function builtAreas() {
  return PORTAL_AREAS.filter((a) => a.built);
}

// ---------------------------------------------------------------------------
// Fase de la estancia, en el idioma del huésped
// ---------------------------------------------------------------------------

/**
 * En qué momento de su estancia está el huésped. Se DERIVA de las fechas
 * (`segmentOf`), no es un campo guardado ni un cuarto eje de estado: §6.2 deja
 * claro que reserva, estancia y cobro son ejes independientes, y el portal no
 * inventa un quinto.
 */
export const STAY_PHASES = [
  "before", "arrival_day", "in_house", "departure_day", "completed", "cancelled",
];

export const STAY_PHASE_LABEL = {
  before: "Próxima estancia",
  arrival_day: "Llegas hoy",
  in_house: "Estancia en curso",
  departure_day: "Hoy es el día de salida",
  completed: "Estancia finalizada",
  cancelled: "Reserva cancelada",
};

/**
 * El traslado, contado al huésped.
 *
 * `TRANSFER_STATUS_LABEL` está escrito para el equipo ("Traslado por su
 * cuenta") y en el portal sonaría a que hablamos de él en tercera persona, en
 * su propia pantalla. Mismo hecho, misma fuente; solo cambia a quién se le
 * cuenta.
 */
export const GUEST_TRANSFER_LABEL = {
  book: "Traslado solicitado",
  own: "El traslado lo organizas tú",
  none: "Dinos si quieres traslado",
};

// ---------------------------------------------------------------------------
// Límites de la vista previa
// ---------------------------------------------------------------------------

/**
 * Lo que el portal NO es todavía, dicho donde el huésped lo ve.
 *
 * El enlace real será un token de ≥128 bits, guardado hasheado, atado a una
 * reserva, con caducidad y revocación (§7.3.1). El de aquí es una etiqueta
 * legible y no protege nada.
 */
export const PORTAL_DEMO_NOTE =
  "Vista previa del portal del huésped. El enlace es de demostración: el token "
  + "real irá hasheado, atado a una reserva y con caducidad. Lo que se envíe "
  + "aquí se queda en esta sesión y desaparece al recargar.";

/**
 * El portal todavía no tiene mensajería propia.
 *
 * §7.2 prevé un botón de recepción/WhatsApp durante la estancia, pero el
 * `MessagingProvider` de la fase 5 no existe y el WhatsApp que sí hay en el
 * sistema es el de los leads de venta (§16.7), con otras reglas, otro
 * consentimiento y otra conversación. Usarlo aquí sería meter a un huésped en
 * un embudo comercial. Hasta entonces el portal enseña el contacto que la
 * propiedad tiene configurado en Ajustes y lo dice tal cual.
 */
export const CONTACT_BOUNDARY_NOTE =
  "Todavía no se puede escribir desde el portal. Estos son los contactos de la "
  + "propiedad configurados en Ajustes.";
